function norm(value) {
  return String(value || '').trim().toUpperCase();
}

/**
 * Normalized GRN status.
 *
 * Reads `grn.status` only — never `grn.posting_status || grn.status`. That fallback is a
 * display idiom used by the chips; `posting_status` belongs to the post-to-stock response,
 * not the GRN row, so it must not drive an action gate.
 */
export function grnStatusOf(grn) {
  const status = norm(grn?.status);
  return status === 'QC_COMPLETE' ? 'QC_COMPLETED' : status;
}

/** Statuses the backend accepts for soft-delete (grn_service.delete). */
export const GRN_CANCELLABLE_STATUSES = ['DRAFT', 'RECEIVED'];

/**
 * A GRN that still has an unsettled claim on its purchase order. POSTED_TO_STOCK is settled
 * (the PO already took the quantity) and CANCELLED is settled (it never will).
 */
export const GRN_IN_FLIGHT_STATUSES = ['DRAFT', 'RECEIVED', 'QC_PENDING', 'QC_COMPLETED'];

export function isGrnInFlight(grn) {
  if (!grn || grn.is_active === false) return false;
  return GRN_IN_FLIGHT_STATUSES.includes(grnStatusOf(grn));
}

export function getActiveGrns(grns = []) {
  return (Array.isArray(grns) ? grns : []).filter(isGrnInFlight);
}

/**
 * QC is finished, so the GRN is a closed document: the list row and the detail header drop to
 * view-only. POSTED is the short alias grnEntity.isGrnPosted() also accepts.
 */
export const GRN_ACTION_LOCKED_STATUSES = ['QC_COMPLETED', 'POSTED_TO_STOCK', 'POSTED'];

export function isGrnActionLocked(grn) {
  return GRN_ACTION_LOCKED_STATUSES.includes(grnStatusOf(grn));
}

/**
 * Why this GRN cannot be cancelled, or null when it can be.
 *
 * Mirrors the guards in grn_service.delete() so the user is told up front instead of after a
 * 422/409. Cancel and delete are the same server operation — soft-delete sets both
 * is_active=false and status=CANCELLED.
 */
export function getGrnCancelBlockReason(grn) {
  const status = grnStatusOf(grn);
  if (!status) return 'This GRN cannot be cancelled because its status is unavailable.';
  if (status === 'CANCELLED' || grn?.is_active === false) {
    return 'This GRN is already cancelled.';
  }
  if (status === 'POSTED_TO_STOCK') {
    return 'This GRN cannot be cancelled or deleted because it has already been posted to stock. Raise a vendor return instead.';
  }
  if (status === 'QC_COMPLETED') {
    return 'This GRN cannot be cancelled or deleted because QC has already been completed.';
  }
  if (status === 'QC_PENDING') {
    return 'This GRN cannot be cancelled or deleted because quality check has already started.';
  }
  if (!GRN_CANCELLABLE_STATUSES.includes(status)) {
    return `A GRN in status ${status.replace(/_/g, ' ')} cannot be cancelled or deleted.`;
  }
  return null;
}

export function canCancelGrn(grn) {
  return getGrnCancelBlockReason(grn) === null;
}

/**
 * Why an in-flight GRN blocks the PO's terminating actions, or null when none does.
 *
 * Short-closing or cancelling a PO writes off its remaining balance. Doing that while goods
 * are received but not yet posted strands the GRN, and procurement-service does not check for
 * GRNs itself (ApprovalService.cancel/close), so this gate only exists here.
 */
export function getPoGrnBlockReason(grns = []) {
  const active = getActiveGrns(grns);
  if (!active.length) return null;
  const numbers = active.map(g => g.grn_number).filter(Boolean).join(', ');
  return `This PO cannot be short cancelled because an active GRN exists${numbers ? ` (${numbers})` : ''}. Please complete the GRN stock posting process first.`;
}
